import type { ProfileTemplate } from '@up2stay/up2promo-api-client';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  cn,
} from '@up2stay/up2promo-ui';
import { useMemo } from 'react';

import { triggerSelectionHaptic } from '@/core/lib/dnd-haptics';

export type TeamTemplateComboboxProps = {
  templates: ProfileTemplate[];
  selectedTemplateId: string | null;
  triggerLabel: string;
  triggerClassName: string;
  disabled?: boolean;
  placeholder: string;
  searchPlaceholder: string;
  emptyMessage: string;
  noneLabel: string;
  locale: string;
  onSelect: (templateId: string | null) => void;
};

type TemplatePickerItem = {
  id: string | null;
  name: string;
};

export function TeamTemplateCombobox({
  templates,
  selectedTemplateId,
  triggerLabel,
  triggerClassName,
  disabled,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  noneLabel,
  locale,
  onSelect,
}: TeamTemplateComboboxProps) {
  const items = useMemo<TemplatePickerItem[]>(() => {
    const collator = locale === 'de' ? 'de' : 'en';
    const sorted = [...templates].sort((a, b) =>
      a.name.localeCompare(b.name, collator, { sensitivity: 'base' }),
    );
    return [
      { id: null, name: noneLabel },
      ...sorted.map((t) => ({ id: t.id, name: t.name })),
    ];
  }, [templates, locale, noneLabel]);

  const selected = useMemo(
    () =>
      items.find((item) => item.id === selectedTemplateId) ?? items[0] ?? null,
    [items, selectedTemplateId],
  );

  const displayLabel = triggerLabel.trim() ? triggerLabel : placeholder;

  return (
    <Combobox
      items={items}
      value={selected}
      onValueChange={(next) => {
        if (!next) return;
        void triggerSelectionHaptic();
        onSelect(next.id);
      }}
      itemToStringLabel={(item) => item.name}
      isItemEqualToValue={(a, b) => a.id === b.id}
      disabled={disabled}
    >
      <ComboboxTrigger
        render={
          <button
            type="button"
            className={cn(
              triggerClassName,
              disabled && 'cursor-not-allowed opacity-60',
            )}
          />
        }
      >
        <span className="min-w-0 flex-1 truncate text-left">
          {displayLabel}
        </span>
      </ComboboxTrigger>
      <ComboboxContent className="max-w-[min(100vw-2rem,320px)]" align="start">
        <ComboboxInput placeholder={searchPlaceholder} showTrigger={false} />
        <ComboboxEmpty>{emptyMessage}</ComboboxEmpty>
        <ComboboxList>
          {(item: TemplatePickerItem) => (
            <ComboboxItem key={item.id ?? '__none__'} value={item}>
              <span
                className={cn(
                  'truncate',
                  item.id == null && 'text-muted-foreground',
                )}
              >
                {item.name}
              </span>
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}
